import React, { useEffect, useState, memo } from 'react';
import { Heart, Star, Trophy, Gift, Diamond, CheckCircle2, Clock, Sparkles, LockKeyhole } from 'lucide-react';
interface MemoryCard {
  id: number;
  imageUrl: string;
  matched: boolean;
  flipped: boolean;
  level: number;
}
interface Task {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  reward: number;
  icon: React.ReactNode;
}
export const AngelMatchGame = () => {
  const [cards, setCards] = useState<MemoryCard[]>([]);
  const [flippedCards, setFlippedCards] = useState<number[]>([]);
  const [matchedPairs, setMatchedPairs] = useState<number>(0);
  const [moves, setMoves] = useState<number>(0);
  const [gameLevel, setGameLevel] = useState<number>(1);
  const [xpPoints, setXpPoints] = useState<number>(0);
  const [showLevelComplete, setShowLevelComplete] = useState<boolean>(false);
  const [showTask, setShowTask] = useState<boolean>(false);
  const [currentTask, setCurrentTask] = useState<Task | null>(null);
  const [showGrandPrize, setShowGrandPrize] = useState<boolean>(false);
  const [isGemPurchased, setIsGemPurchased] = useState<boolean>(false);
  const [gameCompleted, setGameCompleted] = useState<boolean>(false);
  const [timeLeft, setTimeLeft] = useState<number>(60);
  const [isTimerRunning, setIsTimerRunning] = useState<boolean>(false);
  const [maschaMessages, setMaschaMessages] = useState<string[]>(["Hi there! I'm Mascha. Let's play a little memory game...", "Good match! You're quite good at this...", "Mmm, you're making me excited with your skills!", "Another perfect match! I'm starting to feel warm...", "You're so focused! I like a man who pays attention to details..."]);
  const [currentMessage, setCurrentMessage] = useState<string>(maschaMessages[0]);
  // Card images for different levels
  const cardImages = [
  // Level 1 - Professional Mascha
  ['https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=1288&auto=format&fit=crop', 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=1287&auto=format&fit=crop', 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=1364&auto=format&fit=crop', 'https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?q=80&w=1287&auto=format&fit=crop', 'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?q=80&w=1287&auto=format&fit=crop', 'https://images.unsplash.com/photo-1580489944761-15a19d654956?q=80&w=1361&auto=format&fit=crop'],
  // Level 2 - More casual Mascha
  ['https://images.unsplash.com/photo-1524250502761-1ac6f2e30d43?q=80&w=1288&auto=format&fit=crop', 'https://images.unsplash.com/photo-1517841905240-472988babdf9?q=80&w=1287&auto=format&fit=crop', 'https://images.unsplash.com/photo-1524502397800-2eeaad7c3fe5?q=80&w=1287&auto=format&fit=crop', 'https://images.unsplash.com/photo-1464863979621-258859e62245?q=80&w=1286&auto=format&fit=crop', 'https://images.unsplash.com/photo-1488161628813-04466f872be2?q=80&w=1364&auto=format&fit=crop', 'https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?q=80&w=1470&auto=format&fit=crop'],
  // Level 3 - Flirty Mascha
  ['https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?q=80&w=1287&auto=format&fit=crop', 'https://images.unsplash.com/photo-1502323777036-f29e3972d82f?q=80&w=1470&auto=format&fit=crop', 'https://images.unsplash.com/photo-1484399172022-gd6f5a5ba47a?q=80&w=1470&auto=format&fit=crop', 'https://images.unsplash.com/photo-1496440737103-cd596325d314?q=80&w=1287&auto=format&fit=crop', 'https://images.unsplash.com/photo-1492106087820-71f1a00d2b11?q=80&w=1287&auto=format&fit=crop', 'https://images.unsplash.com/photo-1530785602389-07594beb8b73?q=80&w=1287&auto=format&fit=crop']];
  // Tasks that need to be completed to progress
  const tasks: Task[] = [{
    id: 'memory_mastery',
    title: 'Memory Mastery',
    description: 'Complete 3 memory levels without errors',
    completed: false,
    reward: 50,
    icon: <Trophy size={18} className="text-amber-500" data-id="element-1492" />
  }, {
    id: 'rate_angel',
    title: 'Rate an Angel Skin',
    description: 'Show your good taste! Mascha will be watching...',
    completed: false,
    reward: 25,
    icon: <Star size={18} className="text-amber-500" data-id="element-1493" />
  }, {
    id: 'share_avatar',
    title: 'Share AI Avatar',
    description: 'Share an AI-generated avatar on your profile',
    completed: false,
    reward: 30,
    icon: <Sparkles size={18} className="text-purple-500" data-id="element-1494" />
  }, {
    id: 'buy_gems',
    title: 'Unlock Premium Content',
    description: "Buy gems to unlock Mascha's ultimate surprise",
    completed: isGemPurchased,
    reward: 100,
    icon: <Diamond size={18} className="text-blue-500" data-id="element-1495" />
  }, {
    id: 'dickpic_challenge',
    title: 'Participation Challenge',
    description: 'Participate in 1 DickPic Challenge or AI poll',
    completed: false,
    reward: 40,
    icon: <Heart size={18} className="text-pink-500" fill="currentColor" data-id="element-1496" />
  }];
  // Initialize the game with cards
  useEffect(() => {
    initializeGame();
  }, [gameLevel]);
  // Timer effect
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isTimerRunning && timeLeft > 0) {
      timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      // Time's up logic
      setIsTimerRunning(false);
      setShowTask(true);
      setCurrentTask(tasks[Math.floor(Math.random() * tasks.length)]);
    }
    return () => clearTimeout(timer);
  }, [timeLeft, isTimerRunning]);
  // Check for game completion
  useEffect(() => {
    if (matchedPairs === cards.length / 2 && cards.length > 0) {
      // Level completed
      const levelXP = 50 + gameLevel * 25 - Math.floor(moves / 2);
      setXpPoints(xpPoints + levelXP);
      setShowLevelComplete(true);
      setIsTimerRunning(false);
      // Random chance to show a task
      if (Math.random() > 0.5 && !gameCompleted) {
        setTimeout(() => {
          setShowLevelComplete(false);
          setShowTask(true);
          setCurrentTask(tasks[Math.floor(Math.random() * tasks.length)]);
        }, 2000);
      } else if (gameLevel >= 3) {
        // Game completed
        setGameCompleted(true);
      }
    }
  }, [matchedPairs, cards.length]);
  const initializeGame = () => {
    // Get images for current level
    const levelImages = cardImages[Math.min(gameLevel - 1, cardImages.length - 1)];
    // Create pairs of cards
    let cardPairs: MemoryCard[] = [];
    // Number of pairs increases with level
    const pairsCount = Math.min(levelImages.length, 4 + gameLevel);
    for (let i = 0; i < pairsCount; i++) {
      const id1 = i * 2;
      const id2 = i * 2 + 1;
      cardPairs.push({
        id: id1,
        imageUrl: levelImages[i],
        matched: false,
        flipped: false,
        level: gameLevel
      });
      cardPairs.push({
        id: id2,
        imageUrl: levelImages[i],
        matched: false,
        flipped: false,
        level: gameLevel
      });
    }
    // Shuffle cards
    cardPairs = shuffleCards(cardPairs);
    setCards(cardPairs);
    setFlippedCards([]);
    setMatchedPairs(0);
    setMoves(0);
    setTimeLeft(60 + gameLevel * 10); // More time for higher levels
    setIsTimerRunning(true);
  };
  const shuffleCards = (cards: MemoryCard[]) => {
    return [...cards].sort(() => Math.random() - 0.5);
  };
  const handleCardClick = (id: number) => {
    // Ignore click if card is already flipped or matched
    if (flippedCards.length === 2 || cards.find(card => card.id === id)?.flipped || cards.find(card => card.id === id)?.matched) {
      return;
    }
    // Flip the card
    setCards(prevCards => prevCards.map(card => card.id === id ? {
      ...card,
      flipped: true
    } : card));
    // Add to flipped cards
    setFlippedCards(prev => [...prev, id]);
    // If we have 2 flipped cards, check for a match
    if (flippedCards.length === 1) {
      setMoves(moves + 1);
      const firstCardId = flippedCards[0];
      const secondCardId = id;
      const firstCard = cards.find(card => card.id === firstCardId);
      const secondCard = cards.find(card => card.id === secondCardId);
      if (firstCard?.imageUrl === secondCard?.imageUrl) {
        // Match found
        setMatchedPairs(matchedPairs + 1);
        setCards(prevCards => prevCards.map(card => card.id === firstCardId || card.id === secondCardId ? {
          ...card,
          matched: true
        } : card));
        // Show a random Mascha message
        setCurrentMessage(maschaMessages[Math.floor(Math.random() * maschaMessages.length)]);
        // Reset flipped cards
        setFlippedCards([]);
      } else {
        // No match, flip back after delay
        setTimeout(() => {
          setCards(prevCards => prevCards.map(card => card.id === firstCardId || card.id === secondCardId ? {
            ...card,
            flipped: false
          } : card));
          setFlippedCards([]);
        }, 1000);
      }
    }
  };
  const handleNextLevel = () => {
    setShowLevelComplete(false);
    setGameLevel(gameLevel + 1);
  };
  const handleCompleteTask = () => {
    if (currentTask) {
      // Update task completion
      const updatedTasks = tasks.map(task => task.id === currentTask.id ? {
        ...task,
        completed: true
      } : task);
      // Add XP for completing task
      setXpPoints(xpPoints + currentTask.reward);
      // Hide task modal
      setShowTask(false);
      // Check if all tasks are completed to show grand prize
      const allTasksCompleted = updatedTasks.every(task => task.completed);
      if (allTasksCompleted && isGemPurchased) {
        setTimeout(() => {
          setShowGrandPrize(true);
        }, 1000);
      } else {
        // Continue game
        handleNextLevel();
      }
    }
  };
  const handlePurchaseGems = () => {
    setIsGemPurchased(true);
    // Update the buy gems task
    const buyGemsTask = tasks.find(task => task.id === 'buy_gems');
    if (buyGemsTask) {
      buyGemsTask.completed = true;
    }
    // Check if all tasks are completed
    const allTasksCompleted = tasks.every(task => task.completed);
    if (allTasksCompleted) {
      setShowGrandPrize(true);
    }
  };
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };
  return <div className="w-full max-w-4xl mx-auto" data-id="element-1497">
      {/* Game Header */}
      <div className="mb-6 bg-gradient-to-r from-purple-900/20 to-pink-900/20 rounded-xl p-4 border border-pink-500/30" data-id="element-1498">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-1499">
          <div className="flex items-center gap-3" data-id="element-1500">
            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-pink-500" data-id="element-1501">
              <img src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=1288&auto=format&fit=crop" alt="Mascha" className="w-full h-full object-cover" data-id="element-1502" />
            </div>
            <div data-id="element-1503">
              <h2 className="text-xl font-bold" data-id="element-1504">Angel Match Quest</h2>
              <p className="text-sm text-gray-300" data-id="element-1505">with Mascha</p>
            </div>
          </div>
          <div className="flex flex-col w-full md:w-auto" data-id="element-1506">
            {/* XP Progress Bar */}
            <div className="flex items-center gap-2 mb-1" data-id="element-1507">
              <Sparkles size={16} className="text-yellow-400" data-id="element-1508" />
              <span className="text-sm" data-id="element-1509">
                Level {gameLevel} - XP: {xpPoints}
              </span>
              <span className="ml-auto text-sm" data-id="element-1510">
                {timeLeft > 0 ? formatTime(timeLeft) : "Time's up!"}
              </span>
            </div>
            <div className="w-full md:w-64 h-2 bg-gray-700 rounded-full overflow-hidden" data-id="element-1511">
              <div className="h-full bg-gradient-to-r from-pink-500 to-purple-500 rounded-full" style={{
              width: `${Math.min(xpPoints / 500 * 100, 100)}%`
            }} data-id="element-1512"></div>
            </div>
          </div>
        </div>
        {/* Mascha's Message */}
        <div className="mt-3 p-3 bg-pink-900/20 rounded-lg border border-pink-500/20 relative" data-id="element-1513">
          <div className="absolute -top-2 -left-2 w-4 h-4 bg-pink-500 rounded-full" data-id="element-1514"></div>
          <p className="text-sm italic text-gray-200" data-id="element-1515">{currentMessage}</p>
        </div>
      </div>
      {/* Game Board */}
      <div className={`grid grid-cols-${Math.min(4, Math.ceil(Math.sqrt(cards.length)))} gap-3 mb-6`} data-id="element-1516">
        {cards.map(card => <div key={card.id} className={`aspect-square relative cursor-pointer transition-all duration-300 transform ${card.flipped || card.matched ? 'rotate-y-180' : ''} ${card.matched ? 'opacity-80' : ''}`} onClick={() => handleCardClick(card.id)} data-id="element-1517">
            {/* Card Back */}
            <div className={`absolute inset-0 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center shadow-md ${card.flipped || card.matched ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300 backface-hidden`} data-id="element-1518">
              <Heart size={32} className="text-white" fill="white" data-id="element-1519" />
            </div>
            {/* Card Front */}
            <div className={`absolute inset-0 bg-white rounded-xl overflow-hidden shadow-lg ${card.flipped || card.matched ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300 backface-hidden rotate-y-180`} data-id="element-1520">
              <img src={card.imageUrl} alt="Card" className="w-full h-full object-cover" data-id="element-1521" />
              {card.matched && <div className="absolute inset-0 bg-green-500/20 flex items-center justify-center" data-id="element-1522">
                  <CheckCircle2 size={40} className="text-green-500" data-id="element-1523" />
                </div>}
            </div>
          </div>)}
      </div>
      {/* Game Stats */}
      <div className="flex flex-wrap justify-between gap-3 mb-6" data-id="element-1524">
        <div className="bg-gray-800/60 rounded-lg p-3 flex items-center gap-2" data-id="element-1525">
          <Trophy size={18} className="text-yellow-400" data-id="element-1526" />
          <span data-id="element-1527">Moves: {moves}</span>
        </div>
        <div className="bg-gray-800/60 rounded-lg p-3 flex items-center gap-2" data-id="element-1528">
          <Star size={18} className="text-yellow-400" data-id="element-1529" />
          <span data-id="element-1530">
            Pairs: {matchedPairs}/{cards.length / 2}
          </span>
        </div>
        <div className="bg-gray-800/60 rounded-lg p-3 flex items-center gap-2" data-id="element-1531">
          <Clock size={18} className="text-blue-400" data-id="element-1532" />
          <span data-id="element-1533">Time: {formatTime(timeLeft)}</span>
        </div>
      </div>
      {/* Tasks Progress */}
      <div className="bg-gradient-to-r from-gray-900/60 to-purple-900/30 rounded-xl p-4 border border-purple-500/30 mb-6" data-id="element-1534">
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2" data-id="element-1535">
          <CheckCircle2 size={18} className="text-purple-400" data-id="element-1536" />
          <span data-id="element-1537">Mascha's Challenges</span>
        </h3>
        <div className="space-y-3" data-id="element-1538">
          {tasks.map(task => <div key={task.id} className="flex items-center gap-3" data-id="element-1539">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${task.completed ? 'bg-green-500/20 text-green-400' : 'bg-gray-700/50 text-gray-400'}`} data-id="element-1540">
                {task.completed ? <CheckCircle2 size={16} data-id="element-1541" /> : task.icon}
              </div>
              <div className="flex-1" data-id="element-1542">
                <div className="flex items-center justify-between" data-id="element-1543">
                  <p className="text-sm font-medium" data-id="element-1544">{task.title}</p>
                  <span className="text-xs text-purple-400" data-id="element-1545">
                    +{task.reward} XP
                  </span>
                </div>
                <p className="text-xs text-gray-400" data-id="element-1546">{task.description}</p>
              </div>
              {task.id === 'buy_gems' && !isGemPurchased && <button className="px-3 py-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full text-xs text-white" onClick={handlePurchaseGems} data-id="element-1547">
                  Buy Gems
                </button>}
            </div>)}
        </div>
      </div>
      {/* Grand Prize Teaser */}
      <div className="bg-gradient-to-r from-amber-100 to-pink-100 dark:from-amber-900/30 dark:to-pink-900/30 rounded-xl p-4 border border-amber-300 dark:border-amber-500/30 relative overflow-hidden" data-id="element-1548">
        <div className="absolute -right-6 -top-6 w-24 h-24 bg-gradient-to-br from-yellow-300 to-amber-500 dark:from-yellow-400 dark:to-amber-600 rounded-full opacity-20 blur-xl" data-id="element-1549"></div>
        <div className="flex items-center gap-4" data-id="element-1550">
          <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-pink-500 rounded-full flex items-center justify-center" data-id="element-1551">
            <Gift size={24} className="text-white" data-id="element-1552" />
          </div>
          <div className="flex-1" data-id="element-1553">
            <h3 className="text-lg font-bold text-amber-800 dark:text-amber-200" data-id="element-1554">
              Mascha's Ultimate Fantasy
            </h3>
            <p className="text-sm text-amber-700 dark:text-gray-300" data-id="element-1555">
              Complete all challenges to unlock Mascha's most intimate fantasy
              experience
            </p>
          </div>
          <div className="flex flex-col items-center" data-id="element-1556">
            {isGemPurchased ? <button className={`px-4 py-2 bg-gradient-to-r from-amber-500 to-pink-500 rounded-lg text-white text-sm font-medium ${tasks.every(t => t.completed) ? '' : 'opacity-50 cursor-not-allowed'}`} disabled={!tasks.every(t => t.completed)} onClick={() => setShowGrandPrize(true)} data-id="element-1557">
                {tasks.every(t => t.completed) ? 'Claim Prize' : 'Complete Tasks'}
              </button> : <div className="flex items-center gap-1 text-amber-700 dark:text-gray-400" data-id="element-1558">
                <LockKeyhole size={16} data-id="element-1559" />
                <span className="text-sm" data-id="element-1560">Locked</span>
              </div>}
          </div>
        </div>
      </div>
      {/* Level Complete Modal */}
      {showLevelComplete && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" data-id="element-1561">
          <div className="bg-gradient-to-br from-purple-900 to-pink-900 rounded-xl p-6 max-w-md w-full animate-in zoom-in-95 duration-300 border border-pink-500/30" data-id="element-1562">
            <div className="text-center" data-id="element-1563">
              <div className="w-20 h-20 mx-auto bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center mb-4" data-id="element-1564">
                <Trophy size={40} className="text-white" data-id="element-1565" />
              </div>
              <h2 className="text-2xl font-bold mb-2" data-id="element-1566">
                Level {gameLevel} Complete!
              </h2>
              <p className="text-gray-300 mb-4" data-id="element-1567">
                You've matched all pairs in {moves} moves
              </p>
              <div className="flex items-center justify-center gap-6 mb-6" data-id="element-1568">
                <div className="text-center" data-id="element-1569">
                  <p className="text-sm text-gray-400" data-id="element-1570">XP Earned</p>
                  <p className="text-xl font-bold text-purple-400" data-id="element-1571">
                    +{50 + gameLevel * 25 - Math.floor(moves / 2)}
                  </p>
                </div>
                <div className="h-12 border-r border-gray-700" data-id="element-1572"></div>
                <div className="text-center" data-id="element-1573">
                  <p className="text-sm text-gray-400" data-id="element-1574">Time Left</p>
                  <p className="text-xl font-bold text-blue-400" data-id="element-1575">
                    {formatTime(timeLeft)}
                  </p>
                </div>
              </div>
              <button className="w-full py-3 bg-gradient-to-r from-pink-600 to-purple-600 rounded-lg text-white font-medium" onClick={handleNextLevel} data-id="element-1576">
                {gameLevel >= 3 ? 'Claim Rewards' : 'Next Level'}
              </button>
            </div>
          </div>
        </div>}
      {/* Task Modal */}
      {showTask && currentTask && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" data-id="element-1577">
          <div className="bg-gradient-to-br from-gray-900 to-purple-900 rounded-xl p-6 max-w-md w-full animate-in zoom-in-95 duration-300 border border-purple-500/30" data-id="element-1578">
            <div className="text-center" data-id="element-1579">
              <div className="w-20 h-20 mx-auto bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mb-4" data-id="element-1580">
                {currentTask.icon}
              </div>
              <h2 className="text-2xl font-bold mb-2" data-id="element-1581">Mascha's Challenge</h2>
              <p className="text-gray-300 mb-6" data-id="element-1582">{currentTask.description}</p>
              <div className="flex items-center justify-center gap-4 mb-6" data-id="element-1583">
                <img src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=1288&auto=format&fit=crop" alt="Mascha" className="w-16 h-16 rounded-full object-cover border-2 border-purple-500" data-id="element-1584" />
                <div className="bg-purple-900/40 p-3 rounded-lg border border-purple-500/30 text-left" data-id="element-1585">
                  <p className="text-sm italic" data-id="element-1586">
                    "Come on, honey, one more to go... I know you can do it for
                    me."
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between mb-6" data-id="element-1587">
                <div className="text-left" data-id="element-1588">
                  <p className="text-sm text-gray-400" data-id="element-1589">Reward</p>
                  <p className="text-lg font-bold text-purple-400" data-id="element-1590">
                    +{currentTask.reward} XP
                  </p>
                </div>
                {currentTask.id === 'buy_gems' && !isGemPurchased ? <button className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg text-white text-sm font-medium" onClick={handlePurchaseGems} data-id="element-1591">
                    Buy Gems Now
                  </button> : <button className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg text-white text-sm font-medium" onClick={handleCompleteTask} data-id="element-1592">
                    Complete Challenge
                  </button>}
              </div>
              <button className="w-full py-2 bg-gray-800 rounded-lg text-gray-300 text-sm" onClick={() => {
            setShowTask(false);
            handleNextLevel();
          }} data-id="element-1593">
                Skip for Now
              </button>
            </div>
          </div>
        </div>}
      {/* Grand Prize Modal */}
      {showGrandPrize && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md" data-id="element-1594">
          <div className="bg-gradient-to-br from-purple-900 to-pink-900 rounded-xl p-6 max-w-lg w-full animate-in zoom-in-95 duration-300 border border-pink-500/30" data-id="element-1595">
            <div className="text-center" data-id="element-1596">
              <div className="w-24 h-24 mx-auto bg-gradient-to-r from-amber-500 to-pink-500 rounded-full flex items-center justify-center mb-4" data-id="element-1597">
                <Gift size={48} className="text-white" data-id="element-1598" />
              </div>
              <h2 className="text-2xl font-bold mb-2" data-id="element-1599">
                Mascha's Ultimate Fantasy
              </h2>
              <p className="text-gray-300 mb-6" data-id="element-1600">
                You've unlocked Mascha's most intimate fantasy experience.
                Choose your fantasy scenario:
              </p>
              <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-1601">
                <button className="p-4 bg-gradient-to-br from-pink-900/40 to-purple-900/40 rounded-xl border border-pink-500/30 hover:from-pink-900/60 hover:to-purple-900/60 transition-colors" data-id="element-1602">
                  <div className="w-12 h-12 mx-auto bg-pink-500/20 rounded-full flex items-center justify-center mb-2" data-id="element-1603">
                    <span className="text-2xl" data-id="element-1604">💆‍♀️</span>
                  </div>
                  <p className="font-medium" data-id="element-1605">Sensual Massage</p>
                  <p className="text-xs text-gray-400 mt-1" data-id="element-1606">
                    Let Mascha relieve your stress
                  </p>
                </button>
                <button className="p-4 bg-gradient-to-br from-purple-900/40 to-blue-900/40 rounded-xl border border-purple-500/30 hover:from-purple-900/60 hover:to-blue-900/60 transition-colors" data-id="element-1607">
                  <div className="w-12 h-12 mx-auto bg-purple-500/20 rounded-full flex items-center justify-center mb-2" data-id="element-1608">
                    <span className="text-2xl" data-id="element-1609">👩‍🏫</span>
                  </div>
                  <p className="font-medium" data-id="element-1610">Role Play</p>
                  <p className="text-xs text-gray-400 mt-1" data-id="element-1611">
                    Strict teacher or secretary?
                  </p>
                </button>
                <button className="p-4 bg-gradient-to-br from-red-900/40 to-amber-900/40 rounded-xl border border-red-500/30 hover:from-red-900/60 hover:to-amber-900/60 transition-colors" data-id="element-1612">
                  <div className="w-12 h-12 mx-auto bg-red-500/20 rounded-full flex items-center justify-center mb-2" data-id="element-1613">
                    <span className="text-2xl" data-id="element-1614">🍷</span>
                  </div>
                  <p className="font-medium" data-id="element-1615">Romantic Date</p>
                  <p className="text-xs text-gray-400 mt-1" data-id="element-1616">
                    Intimate candlelit dinner
                  </p>
                </button>
                <button className="p-4 bg-gradient-to-br from-emerald-900/40 to-teal-900/40 rounded-xl border border-emerald-500/30 hover:from-emerald-900/60 hover:to-teal-900/60 transition-colors" data-id="element-1617">
                  <div className="w-12 h-12 mx-auto bg-emerald-500/20 rounded-full flex items-center justify-center mb-2" data-id="element-1618">
                    <span className="text-2xl" data-id="element-1619">🔒</span>
                  </div>
                  <p className="font-medium" data-id="element-1620">Secret Fantasy</p>
                  <p className="text-xs text-gray-400 mt-1" data-id="element-1621">
                    Mascha's special surprise
                  </p>
                </button>
              </div>
              <div className="flex items-center justify-center gap-4 mb-6" data-id="element-1622">
                <img src="https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?q=80&w=1287&auto=format&fit=crop" alt="Mascha" className="w-16 h-16 rounded-full object-cover border-2 border-pink-500" data-id="element-1623" />
                <div className="bg-pink-900/40 p-3 rounded-lg border border-pink-500/30 text-left" data-id="element-1624">
                  <p className="text-sm italic" data-id="element-1625">
                    "I've been waiting for this moment... Which fantasy shall we
                    explore together?"
                  </p>
                </div>
              </div>
              <button className="w-full py-3 bg-gradient-to-r from-pink-600 to-purple-600 rounded-lg text-white font-medium mb-2" onClick={() => setShowGrandPrize(false)} data-id="element-1626">
                Enter Fantasy
              </button>
              <button className="w-full py-2 bg-transparent text-gray-400 text-sm hover:text-gray-300" onClick={() => setShowGrandPrize(false)} data-id="element-1627">
                Maybe Later
              </button>
            </div>
          </div>
        </div>}
      <style jsx data-id="element-1628">{`
        .rotate-y-180 {
          transform: rotateY(180deg);
        }
        .backface-hidden {
          backface-visibility: hidden;
        }
        .grid-cols-2 {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
        .grid-cols-3 {
          grid-template-columns: repeat(3, minmax(0, 1fr));
        }
        .grid-cols-4 {
          grid-template-columns: repeat(4, minmax(0, 1fr));
        }
        .grid-cols-5 {
          grid-template-columns: repeat(5, minmax(0, 1fr));
        }
        .grid-cols-6 {
          grid-template-columns: repeat(6, minmax(0, 1fr));
        }
      `}</style>
    </div>;
};